import React, { useCallback, useMemo, useState } from 'react'
import { Settings, LogOut, HelpCircle, RefreshCw, Globe, Check } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../hooks/useAuth'
import { useLanguage } from '../../../contexts/LanguageProvider'
import { usePreferencesDialog } from '../../../contexts/PreferencesDialogContext'
import { usePlatform } from '../../../hooks/usePlatform'
import { checkForUpdates } from '../../../hooks/useUpdateStatus'
import { HBaseIcon } from '../../icons/HBaseIcon'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../../ui/dropdown-menu'

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
] as const
import avatarDefault from '../../../assets/images/Sidebar/avatar-default.png'
import avatarDefault2x from '../../../assets/images/Sidebar/avatar-default@2x.png'
import './UserAvatar.scss'

interface UserAvatarUser {
  full_name?: string
  email?: string
  avatar_url?: string
  user_metadata?: {
    full_name?: string
    avatar_url?: string
    [key: string]: unknown
  }
}

interface UserAvatarProps {
  user: UserAvatarUser | null | undefined
  avatarUrl?: string | null
  fullName?: string | null
  /**
   * `true` until the avatar fetch resolves. While loading we suppress the
   * <img> entirely so the user never sees the bundled fallback PNG flash
   * before their real picture arrives.
   */
  isLoading?: boolean
}

/**
 * User profile avatar button with dropdown menu in the sidebar footer.
 *
 * Built on the shadcn/Radix DropdownMenu primitive — open/close state is
 * managed internally by Radix.
 */
const UserAvatarComponent: React.FC<UserAvatarProps> = ({
  user,
  avatarUrl,
  fullName,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const { language: activeLanguage, changeLanguage } = useLanguage()
  const navigate = useNavigate()
  const { user: authUser, logout } = useAuth()
  const preferencesDialog = usePreferencesDialog()
  const { isMac } = usePlatform()

  const handleSettings = useCallback(() => {
    if (window.api?.invoke) {
      window.api.invoke('preferences:open')
    } else if (preferencesDialog) {
      preferencesDialog.open()
    }
  }, [preferencesDialog])

  const handleCheckForUpdates = useCallback(() => {
    checkForUpdates()
  }, [])

  const handleLogout = useCallback(async () => {
    try {
      const result = await logout()
      if (result.success) {
        if (window.api?.invoke) {
          await window.api.invoke('transition-to-setup')
        } else {
          navigate('/', { replace: true })
        }
      }
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }, [logout, navigate])

  const handleHelp = useCallback(() => {
    window.open('https://happytask.app/help', '_blank')
  }, [])

  // Persists via platform API (Electron main process / web localStorage) — calling
  // i18n.changeLanguage directly would only update React state and revert on the
  // next platform sync.
  const handleLanguageChange = useCallback((code: string) => {
    changeLanguage(code)
  }, [changeLanguage])

  // activeLanguage can be 'de-DE' etc. — match by primary subtag only.
  const activeLanguageCode = activeLanguage?.split('-')[0] ?? 'en'

  const userName = fullName
    || user?.user_metadata?.full_name
    || user?.full_name
    || t('userMenu.defaultName')
  // Prefer the live Supabase session user — the `user` prop comes from
  // useGlobalUser, which is missing email because the main-process
  // `auth:success` IPC is never emitted (see useUserDisplayProfile).
  const userEmail = authUser?.email || user?.email || ''
  const settingsShortcut = isMac ? '⌘,' : 'Ctrl+,'

  // Track failed remote avatar loads so a 404 / blocked CDN response falls
  // back to the bundled default image instead of showing the broken-image icon.
  const [imageFailed, setImageFailed] = useState(false)
  const remoteSrc = avatarUrl || user?.avatar_url || null
  const useDefault = !remoteSrc || imageFailed

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="user-avatar" title={userName}>
          <span className="user-avatar__hbase user-avatar__hbase--hover">
            <HBaseIcon size={32} color="var(--muted)" />
          </span>
          <span className="user-avatar__hbase user-avatar__hbase--pressed">
            <HBaseIcon size={32} color="var(--border)" />
          </span>
          {!isLoading && (
            <img
              src={useDefault ? avatarDefault : (remoteSrc as string)}
              srcSet={useDefault ? `${avatarDefault} 1x, ${avatarDefault2x} 2x` : undefined}
              alt={userName}
              className="user-avatar__image"
              onError={() => setImageFailed(true)}
            />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={8}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel>
          <div className="user-avatar__menu-name">{userName}</div>
          {userEmail && <div className="user-avatar__menu-email">{userEmail}</div>}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={handleSettings}>
          <Settings size={16} className="context-menu-content__item-icon" />
          <span>{t('userMenu.settings')}</span>
          <DropdownMenuShortcut>{settingsShortcut}</DropdownMenuShortcut>
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Globe size={16} className="context-menu-content__item-icon" />
            <span>{t('userMenu.language')}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {LANGUAGES.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onSelect={() => handleLanguageChange(lang.code)}
              >
                <span>{lang.name}</span>
                {activeLanguageCode === lang.code && (
                  <Check size={14} className="context-menu-content__item-check" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuItem onSelect={handleCheckForUpdates}>
          <RefreshCw size={16} className="context-menu-content__item-icon" />
          <span>{t('userMenu.checkForUpdates')}</span>
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={handleHelp}>
          <HelpCircle size={16} className="context-menu-content__item-icon" />
          <span>{t('userMenu.help')}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={handleLogout}>
          <LogOut size={16} className="context-menu-content__item-icon" />
          <span>{t('userMenu.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const UserAvatar = React.memo(UserAvatarComponent)
